import React, { useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md',
  showCloseButton = true,
  closeOnOverlay = true,
  maxHeight = 'default'
}) => {
  const { isMobile, isTablet, getResponsiveValue } = useTheme();

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Responsive size classes
  const sizeClasses = {
    sm: getResponsiveValue({
      mobile: 'max-w-sm',
      tablet: 'max-w-md',
      desktop: 'max-w-md'
    }),
    md: getResponsiveValue({
      mobile: 'max-w-lg',
      tablet: 'max-w-xl',
      desktop: 'max-w-2xl'
    }),
    lg: getResponsiveValue({
      mobile: 'max-w-2xl',
      tablet: 'max-w-3xl',
      desktop: 'max-w-4xl'
    }),
    xl: getResponsiveValue({
      mobile: 'max-w-4xl',
      tablet: 'max-w-5xl',
      desktop: 'max-w-6xl'
    })
  };

  // Height control classes
  const heightClasses = {
    default: getResponsiveValue({
      mobile: 'max-h-[90vh]',
      desktop: 'max-h-[85vh]'
    }),
    compact: getResponsiveValue({
      mobile: 'max-h-[75vh]',
      desktop: 'max-h-[70vh]'
    }),
    small: getResponsiveValue({
      mobile: 'max-h-[65vh]',
      desktop: 'max-h-[60vh]'
    }),
    medium: getResponsiveValue({
      mobile: 'max-h-[80vh]',
      desktop: 'max-h-[75vh]'
    }),
    large: getResponsiveValue({
      mobile: 'max-h-[95vh]',
      desktop: 'max-h-[90vh]'
    })
  };

  const modalMargin = getResponsiveValue({
    mobile: 'm-4',
    tablet: 'm-6',
    desktop: 'm-8'
  });

  const headerPadding = getResponsiveValue({
    mobile: 'px-4 py-4',
    tablet: 'px-5 py-5',
    desktop: 'px-6 py-6'
  });

  const titleSize = getResponsiveValue({
    mobile: 'text-lg',
    desktop: 'text-xl'
  });

  const borderRadius = getResponsiveValue({
    mobile: 'rounded-xl',
    desktop: 'rounded-2xl'
  });

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlay) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto"
      onClick={handleOverlayClick}
    >
      {/* Backdrop - Fixed positioning with proper overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300" />
      
      {/* Modal Container - Properly centered */}
      <div 
        className={`
          relative bg-surface-elevated shadow-theme-xl transform transition-all duration-300
          w-full ${sizeClasses[size]} ${heightClasses[maxHeight]} ${modalMargin}
          flex flex-col border border-border-primary ${borderRadius}
          animate-in fade-in-0 zoom-in-95 duration-300
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        {(title || showCloseButton) && (
          <div className={`flex items-center justify-between ${headerPadding} border-b border-border-primary flex-shrink-0`}>
            {title && (
              <h3 className={`${titleSize} font-semibold text-text-primary flex-1 ${showCloseButton ? 'pr-4' : ''}`}>
                {title}
              </h3>
            )}
            
            {showCloseButton && (
              <button
                onClick={onClose}
                className="text-text-tertiary hover:text-text-primary transition-colors duration-200 hover:bg-surface-hover rounded-lg p-2 flex-shrink-0"
                aria-label="Close modal"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}
        
        {/* Content - Scrollable */}
        <div className="overflow-y-auto flex-1 min-h-0">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;